/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2026 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { devtools } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import {
  DebtorCollectionDto2,
  deleteRemoveDebtorCollection,
  getShowDebtorCollections,
} from '@/api-sdk';
import { globalStorePropertiesStore } from '@/stores/creators/creator.global-store-properties';
import { GlobalComponentPropertiesTypes } from '@/types';
import { CollectionsAnnouncement } from '@/types/types.collections';

export type CollectionListState = Partial<GlobalComponentPropertiesTypes> & {
  collections: DebtorCollectionDto2[] | undefined;
  total: number;
  pages: number;
  page: number;
  pageSize: number;
  isLoading: boolean;
  announcement: string;
  customers: { [id: string]: string };
  searchTerms?: {
    searchTermsQuery?: string;
    searchTermsCustomers: number[];
    customer?: number;
  };
  createdByMeActive: boolean;

  getCollections: () => Promise<void>;
  handleFilter: (searchString: string, createdByMe: boolean) => void;
  goToPage: (page: number) => void;
  setAnnouncementText: (
    announcement: CollectionsAnnouncement,
    collectionName?: string,
  ) => void;
  deleteCollection: (collectionId: string, collectionName: string) => void;
  setConfig: (config: Partial<CollectionListState>) => void;
};

function checkIfSearchStringIsCustomerName(
  customers: { [id: string]: string },
  searchString: string | null,
): number[] {
  if (!searchString) {
    return [];
  }

  return Object.entries(customers)
    .filter(([, name]) =>
      name.toLowerCase().includes(searchString.toLowerCase()),
    )
    .map(([id]) => Number(id));
}

export const collectionListStore = createStore<CollectionListState>()(
  devtools((set, get) => ({
    ...globalStorePropertiesStore,
    collections: undefined,
    total: 0,
    pages: 1,
    page: 1,
    pageSize: 8,
    isLoading: false,
    announcement: '',
    customers: {},
    searchTerms: undefined,
    createdByMeActive: false,

    getCollections: async () => {
      const {
        customerId,
        debtor,
        locale,
        country,
        page,
        pageSize,
        searchTerms,
      } = get();

      if (!customerId || !debtor || !locale || !country) {
        throw new Error('Missing required parameters');
      }

      try {
        set(
          { isLoading: true },
          false,
          'SET_STATE: [getCollections] Start loading',
        );

        const result = await getShowDebtorCollections({
          debtorCode: debtor,
          params: {
            locale,
            country,
            page,
            pageSize,
            customer: searchTerms?.customer,
            'searchTerms[query]': searchTerms?.searchTermsQuery,
            'searchTerms[customers][]': searchTerms?.searchTermsCustomers,
          },
        });

        set(
          {
            collections: result.collections,
            total: result.total,
            pages: result.pages,
          },
          false,
          'SET_STATE: [getCollections] Update collections data',
        );
      } catch (error) {
        console.error('Error fetching collections', error);
      } finally {
        const { collections, setAnnouncementText } = get();

        set(
          { isLoading: false },
          false,
          'SET_STATE: [getCollections] Stop loading',
        );

        setAnnouncementText(
          collections && collections.length > 0
            ? CollectionsAnnouncement.LOADED
            : CollectionsAnnouncement.NO_COLLECTIONS,
        );
      }
    },

    handleFilter: (searchString: string, createdByMe: boolean) => {
      const { customers, customerId, getCollections } = get();

      if (!searchString && !createdByMe) {
        set(
          { searchTerms: undefined, createdByMeActive: false },
          false,
          'SET_STATE: [handleFilter] Clear search terms',
        );
        getCollections();
        return;
      }

      const createdByMeActive = createdByMe;

      const searchTerms = {
        searchTermsQuery: searchString || undefined,
        searchTermsCustomers: !createdByMeActive
          ? checkIfSearchStringIsCustomerName(customers, searchString)
          : [],
        customer: createdByMeActive ? customerId : undefined,
      };

      set(
        { searchTerms, createdByMeActive },
        false,
        'SET_STATE: [handleFilter] Update search terms',
      );

      getCollections();
    },

    goToPage: (page: number) => {
      const { getCollections } = get();

      set({ page }, false, 'SET_STATE: [goToPage] Update page');
      getCollections();
    },

    setAnnouncementText: (
      announcement: CollectionsAnnouncement,
      collectionName = '',
    ) => {
      const { collections, total } = get();

      const announcements: Record<CollectionsAnnouncement, string> = {
        loaded: t('accessibility.collectionsAnnouncement.loaded', {
          totalCollections: collections?.length,
          totalCollectionsToLoad: total,
        }),
        noCollections: t('accessibility.collectionsAnnouncement.noCollections'),
        removed: t('accessibility.collectionsAnnouncement.removed', {
          collectionName,
        }),
      };

      set(
        { announcement: announcements[announcement] || '' },
        false,
        'SET_STATE: [setAnnouncementText] Update announcement',
      );
    },

    deleteCollection: async (collectionId: string, collectionName: string) => {
      const { setAnnouncementText, debtor, getCollections } = get();
      if (!debtor || !collectionId) {
        return;
      }
      try {
        await deleteRemoveDebtorCollection({
          debtorCode: debtor,
          collectionId,
        });
        setAnnouncementText(CollectionsAnnouncement.REMOVED, collectionName);
      } catch (error) {
        console.error('Error deleting collection', error);
      } finally {
        getCollections();
      }
    },

    setConfig: config =>
      set(config, false, 'SET_STATE: [setConfig] Update config properties'),
  })),
);
